import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Send, User, Bot, Mail, Phone, Building } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { GlowingCard } from '../../components/3DEffects';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminTicketDetail() {
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchTicket();
  }, [ticketId]);

  const fetchTicket = async () => {
    try {
      const res = await fetch(`${API}/admin/tickets/${ticketId}`, { credentials: 'include' });
      if (res.ok) {
        setTicket(await res.json());
      }
    } catch (error) {
      console.error('Failed to fetch ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (newStatus) => {
    try {
      const res = await fetch(`${API}/admin/tickets/${ticketId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        setTicket({ ...ticket, status: newStatus });
        toast.success('Status updated');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;

    setSending(true);
    try {
      const res = await fetch(`${API}/admin/tickets/${ticketId}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: reply })
      });

      if (res.ok) {
        const newMsg = await res.json();
        setTicket({
          ...ticket,
          messages: [...(ticket.messages || []), newMsg],
          status: ticket.status === 'open' ? 'in_progress' : ticket.status
        });
        setReply('');
        toast.success('Reply sent');
      }
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30',
      in_progress: 'text-blue-400 bg-blue-400/10 border-blue-400/30',
      resolved: 'text-green-400 bg-green-400/10 border-green-400/30',
      closed: 'text-gray-400 bg-gray-400/10 border-gray-400/30'
    };
    return colors[status] || 'text-gray-400 bg-gray-400/10';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-16">
        <h2 className="text-xl font-bold text-white mb-4">Ticket Not Found</h2>
        <Link to="/admin/tickets" className="text-blue-400 hover:underline">
          Back to Tickets
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="admin-ticket-detail">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <Link 
          to="/admin/tickets"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Tickets
        </Link>

        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">{ticket.subject}</h1>
            <div className="flex flex-wrap items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(ticket.status)}`}>
                {ticket.status.replace('_', ' ')}
              </span>
              <span className="px-3 py-1 rounded-full text-sm bg-white/5 text-gray-400">
                {ticket.category}
              </span>
              <span className="px-3 py-1 rounded-full text-sm bg-white/5 text-gray-400">
                {ticket.priority} priority
              </span>
            </div>
          </div>
          <Select value={ticket.status} onValueChange={updateStatus}>
            <SelectTrigger className="w-[160px] bg-white/5 border-white/10 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0D1224] border-white/10">
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </motion.div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <GlowingCard className="p-6 rounded-xl">
              <h2 className="text-lg font-semibold text-white mb-4">Description</h2>
              <p className="text-gray-400 whitespace-pre-wrap">{ticket.description}</p>
              <div className="mt-4 pt-4 border-t border-white/10 text-sm text-gray-500">
                <Clock className="w-4 h-4 inline mr-2" />
                Created {formatDate(ticket.created_at)}
              </div>
            </GlowingCard>
          </motion.div>

          {/* Conversation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <GlowingCard className="p-6 rounded-xl">
              <h2 className="text-lg font-semibold text-white mb-4">Conversation</h2>

              <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
                {(!ticket.messages || ticket.messages.length === 0) ? (
                  <p className="text-gray-500 text-center py-8">No messages yet</p>
                ) : (
                  ticket.messages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className={`flex gap-3 ${msg.is_staff ? 'flex-row-reverse' : ''}`}
                    >
                      <div className={`
                        w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
                        ${msg.is_staff ? 'bg-blue-500/20' : 'bg-white/10'}
                      `}>
                        {msg.is_staff ? (
                          <Bot className="w-5 h-5 text-blue-400" />
                        ) : (
                          <User className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                      <div className={`
                        max-w-[80%] p-4 rounded-xl
                        ${msg.is_staff 
                          ? 'bg-blue-500/10 text-gray-200 rounded-tr-md border border-blue-500/20' 
                          : 'bg-white/5 text-gray-300 rounded-tl-md'
                        }
                      `}>
                        <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                        <span className="text-xs text-gray-500 mt-2 block">
                          {msg.is_staff ? 'Staff' : 'Customer'} • {formatDate(msg.created_at)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Reply Input */}
              {ticket.status !== 'closed' && (
                <form onSubmit={sendReply} className="flex gap-3">
                  <Textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="Type your reply..."
                    rows={2}
                    className="flex-1 bg-white/5 border-white/10 text-white resize-none"
                  />
                  <Button
                    type="submit"
                    disabled={sending || !reply.trim()}
                    className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-6 border-none"
                  >
                    {sending ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </form>
              )}
            </GlowingCard>
          </motion.div>
        </div>

        {/* Sidebar - Customer Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <GlowingCard className="p-6 rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Customer Info</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                  <User className="w-6 h-6 text-gray-400" />
                </div>
                <div>
                  <p className="text-white font-medium">{ticket.user?.name || 'Unknown'}</p>
                  <p className="text-gray-500 text-sm">Customer</p>
                </div>
              </div>
              
              <div className="space-y-3 pt-4 border-t border-white/10">
                {ticket.user?.email && (
                  <div className="flex items-center gap-3 text-sm">
                    <Mail className="w-4 h-4 text-blue-400" />
                    <span className="text-gray-400">{ticket.user.email}</span>
                  </div>
                )}
                {ticket.user?.phone && (
                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="w-4 h-4 text-blue-400" />
                    <span className="text-gray-400">{ticket.user.phone}</span>
                  </div>
                )}
                {ticket.user?.company && (
                  <div className="flex items-center gap-3 text-sm">
                    <Building className="w-4 h-4 text-blue-400" />
                    <span className="text-gray-400">{ticket.user.company}</span>
                  </div>
                )}
              </div>
            </div>
          </GlowingCard>

          {/* Ticket Info */}
          <GlowingCard className="p-6 rounded-xl mt-6">
            <h3 className="text-lg font-semibold text-white mb-4">Ticket Info</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">ID</span>
                <span className="text-white font-mono text-xs">{ticket.ticket_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Category</span>
                <span className="text-white">{ticket.category}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Priority</span>
                <span className="text-white capitalize">{ticket.priority}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Created</span>
                <span className="text-white">{formatDate(ticket.created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Updated</span>
                <span className="text-white">{formatDate(ticket.updated_at)}</span>
              </div>
            </div>
          </GlowingCard>
        </motion.div>
      </div>
    </div>
  );
}
